/**
 * Fetch / state logic for the Catalog Issues tab.
 *
 * Per the frontend design doc this tab keeps its state in LOCAL component
 * state (React hooks), not Redux. A degraded catalog dependency never fails
 * the HTTP call - the BFF always answers 200 with a `dependencyStatus`
 * field - so callers must branch on `dependencyStatus`, and only a genuine
 * transport error puts these hooks into the `failed` state.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCatalogIssues, searchCatalogIssues } from "../../app/api";
import {
  AsinWithCatalogIssues,
  CatalogIssueDetailResult,
  CatalogIssuesResult,
} from "../../../from-server/asin-central/catalog-issues";
import { isNoFilterApplied } from "../../searchSideBar/queryStringHelpers";
import logger from "../../../art-logger-config";

export type CatalogIssuesFetchStatus = "idle" | "loading" | "loaded" | "failed";

export interface CatalogIssuesSearchState {
  readonly status: CatalogIssuesFetchStatus;
  readonly result?: CatalogIssuesResult;
}

export interface CatalogIssueDetailState {
  readonly status: CatalogIssuesFetchStatus;
  /** ASIN the detail pane is showing (set as soon as a row is selected). */
  readonly asin?: string;
  readonly marketplace?: string;
  readonly result?: CatalogIssueDetailResult;
}

/**
 * Client-side filter over the loaded rows: case-insensitive substring match
 * on ASIN or description. Does NOT re-query the backend.
 */
export function filterAsinsWithIssues(
  rows: readonly AsinWithCatalogIssues[],
  filterText: string
): AsinWithCatalogIssues[] {
  const needle = filterText.trim().toLowerCase();
  if (needle === "") {
    return [...rows];
  }
  return rows.filter(
    (row) =>
      row.asin.toLowerCase().includes(needle) ||
      row.description.toLowerCase().includes(needle)
  );
}

/**
 * Loads the catalog-issues search result whenever the submitted global
 * filter (`queryString`) changes. Skips the sentinel "no filter applied"
 * values. Errors are contained here so a failure in this tab can never
 * break the rest of the Validation page.
 */
export function useCatalogIssuesSearch(
  queryString: string | undefined,
  mockState?: string
): CatalogIssuesSearchState {
  const [state, setState] = useState<CatalogIssuesSearchState>({
    status: "idle",
  });

  useEffect(() => {
    if (isNoFilterApplied(queryString)) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    searchCatalogIssues(queryString as string, mockState)
      .then((result) => {
        if (!cancelled) {
          setState({ status: "loaded", result: result });
        }
      })
      .catch((error: unknown) => {
        logger.error(`Failed to load catalog issues: ${String(error)}`);
        if (!cancelled) {
          setState({ status: "failed" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [queryString, mockState]);

  return state;
}

export interface CatalogIssueDetailHook {
  readonly state: CatalogIssueDetailState;
  /** Lazily fetch the issue detail for the row the user clicked. */
  readonly select: (asin: string, marketplace: string) => void;
  /** Clear the detail pane and invalidate any in-flight detail request. */
  readonly reset: () => void;
}

export function useCatalogIssueDetail(): CatalogIssueDetailHook {
  const [state, setState] = useState<CatalogIssueDetailState>({
    status: "idle",
  });
  // Monotonic request id so a slow response for a previously-selected row
  // can never overwrite the detail of the row selected afterwards.
  const requestIdRef = useRef(0);

  const select = useCallback((asin: string, marketplace: string) => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setState({ status: "loading", asin: asin, marketplace: marketplace });
    getCatalogIssues(asin, marketplace)
      .then((result) => {
        if (requestIdRef.current === requestId) {
          setState({
            status: "loaded",
            asin: asin,
            marketplace: marketplace,
            result: result,
          });
        }
      })
      .catch((error: unknown) => {
        logger.error(
          `Failed to load catalog issue detail for ${asin}/${marketplace}: ${String(
            error
          )}`
        );
        if (requestIdRef.current === requestId) {
          setState({ status: "failed", asin: asin, marketplace: marketplace });
        }
      });
  }, []);

  // Clears the detail pane and bumps the request id so a slow in-flight
  // request for a now-stale selection cannot repopulate the cleared pane.
  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setState({ status: "idle" });
  }, []);

  return { state: state, select: select, reset: reset };
}
